// frontend/src/components/ui/SavedViewMenu.tsx
// Brighella v2 (ADR-013 P8 / ADR-014 D3) — the "Views" dropdown that lets a
// user save / apply / share / pin the current filter+view-mode combo on a hub.
//
// Prop contract is consumed by CalendarHub (and future hubs). Do NOT change
// the SavedViewMenuProps shape without coordinating.

import { useEffect, useRef, useState } from 'react'
import { BookmarkCheck, Star, Trash2, Users, Lock, Plus } from 'lucide-react'
import { useLanguage } from '../../i18n/index.jsx'
import { useSavedViewsStore } from '../../store/savedViewsStore'
import type { SavedView } from '../../store/savedViewsStore'
import './SavedViewMenu.css'

export interface SavedViewMenuProps {
  /** Which hub this menu serves — namespaces stored views server-side. */
  surface: string
  /** The live view state to capture when the user saves a view. */
  currentState: unknown
  /** Called with a previously-saved state when the user applies a view. */
  onApply: (state: unknown) => void
}

// ── Surface-state ↔ URL serialization ──────────────────────────────────────
// A calendar saved-view `state` mirrors the URL params CalendarHub reads:
//   view, q, anchor, venues, productions, people, subjectTypes, categories,
//   statuses. Stored as a flat object; serialized back to a query string for
//   sidebar pins and Cmd+K navigation.
const SURFACE_ROUTES: Record<string, string> = {
  calendar: '/calendar',
  items: '/items',
  loans: '/loans',
  // ContactsDirectory lives inside the admin Organisation tab (local state,
  // not URL-addressable) — a pinned contacts view lands on the admin page.
  contacts: '/admin',
}

/** Bare route for a surface (used when a view's state can't be serialized). */
export function surfaceRoute(surface: string): string {
  return SURFACE_ROUTES[surface] || `/${surface}`
}

/**
 * Build a navigable path for a saved view: its surface route plus the view's
 * state serialized into URL query params. Falls back to the bare route when
 * the state isn't a plain object.
 */
export function viewToPath(view: Pick<SavedView, 'surface' | 'state'>): string {
  const base = surfaceRoute(view.surface)
  const state = view.state
  if (!state || typeof state !== 'object' || Array.isArray(state)) return base
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(state as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) {
      if (v.length) qs.set(k, v.join(','))
    } else {
      qs.set(k, String(v))
    }
  }
  const str = qs.toString()
  return str ? `${base}?${str}` : base
}

export default function SavedViewMenu({ surface, currentState, onApply }: SavedViewMenuProps) {
  const { t } = useLanguage()
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)

  const views = useSavedViewsStore((s) => s.views[surface] || [])
  const loading = useSavedViewsStore((s) => s.loading)
  const saveError = useSavedViewsStore((s) => s.saveError)
  // 409 optimistic-concurrency conflict (finding M3): another user saved this
  // view first. Surfaced as a dedicated reload prompt, distinct from the
  // duplicate-name `saveError`.
  const saveConflict = useSavedViewsStore((s) => s.saveConflict)
  const loadViews = useSavedViewsStore((s) => s.loadViews)
  const createView = useSavedViewsStore((s) => s.createView)
  const updateView = useSavedViewsStore((s) => s.updateView)
  const deleteView = useSavedViewsStore((s) => s.deleteView)
  const setPin = useSavedViewsStore((s) => s.setPin)
  const clearSaveError = useSavedViewsStore((s) => s.clearSaveError)

  // Ensure the surface's views are loaded the first time the menu opens.
  useEffect(() => {
    if (open) loadViews(surface)
  }, [open, surface, loadViews])

  // Outside-click + Escape close.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSaving(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        setSaving(false)
      }
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    document.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (saving) nameInputRef.current?.focus()
  }, [saving])

  const own = views.filter((v) => v.is_own)
  const shared = views.filter((v) => !v.is_own)

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    const created = await createView({
      surface,
      name: trimmed,
      visibility: 'private',
      state: currentState,
    })
    if (created) {
      setSaving(false)
      setName('')
    }
    // On failure, saveError is set by the store and rendered inline below.
  }

  const handleApply = (view: SavedView) => {
    onApply(view.state)
    setOpen(false)
  }

  return (
    <div className="saved-view-menu" ref={rootRef}>
      <button
        type="button"
        className="saved-view-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <BookmarkCheck size={14} aria-hidden="true" />
        <span>{t('savedViewsLabel')}</span>
      </button>

      {open && (
        <div className="saved-view-menu-panel" role="menu" aria-label={t('savedViewsLabel')}>
          {loading && (
            <div className="saved-view-menu-empty">{t('savedViewsLoading')}</div>
          )}

          {/* 409 VERSION_CONFLICT — the view was changed by someone else; the
              last save did NOT apply. Prompt a reload of the surface's views. */}
          {saveConflict && (
            <div className="saved-view-menu-error" role="alert">
              <span>{t('errors.versionConflict')}</span>
              <button
                type="button"
                className="saved-view-menu-save-confirm"
                onClick={() => {
                  clearSaveError()
                  loadViews(surface)
                }}
              >
                {t('refresh')}
              </button>
            </div>
          )}

          {!loading && own.length === 0 && shared.length === 0 && (
            <div className="saved-view-menu-empty">{t('savedViewsEmpty')}</div>
          )}

          {!loading && own.length > 0 && (
            <div className="saved-view-menu-group">
              <div className="saved-view-menu-group-label">
                {t('savedViewsMine')}
              </div>
              {own.map((v) => (
                <div key={v.id} className="saved-view-menu-row">
                  <button
                    type="button"
                    role="menuitem"
                    className="saved-view-menu-apply"
                    onClick={() => handleApply(v)}
                  >
                    {v.name}
                  </button>
                  <button
                    type="button"
                    className="saved-view-menu-icon"
                    aria-pressed={v.pinned}
                    title={v.pinned ? t('savedViewsUnpin') : t('savedViewsPin')}
                    aria-label={v.pinned ? t('savedViewsUnpin') : t('savedViewsPin')}
                    onClick={() => setPin(v.id, !v.pinned)}
                  >
                    <Star
                      size={13}
                      aria-hidden="true"
                      fill={v.pinned ? 'currentColor' : 'none'}
                    />
                  </button>
                  <button
                    type="button"
                    className="saved-view-menu-icon"
                    aria-pressed={v.visibility === 'tenant'}
                    title={
                      v.visibility === 'tenant'
                        ? t('savedViewsMakePrivate')
                        : t('savedViewsShare')
                    }
                    aria-label={
                      v.visibility === 'tenant'
                        ? t('savedViewsMakePrivate')
                        : t('savedViewsShare')
                    }
                    onClick={() =>
                      updateView(v.id, {
                        visibility: v.visibility === 'tenant' ? 'private' : 'tenant',
                      })
                    }
                  >
                    {v.visibility === 'tenant' ? (
                      <Users size={13} aria-hidden="true" />
                    ) : (
                      <Lock size={13} aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    className="saved-view-menu-icon saved-view-menu-delete"
                    title={t('savedViewsDelete')}
                    aria-label={t('savedViewsDelete')}
                    onClick={() => deleteView(v.id)}
                  >
                    <Trash2 size={13} aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {!loading && shared.length > 0 && (
            <div className="saved-view-menu-group">
              <div className="saved-view-menu-group-label">
                {t('savedViewsShared')}
              </div>
              {shared.map((v) => (
                <div key={v.id} className="saved-view-menu-row">
                  <button
                    type="button"
                    role="menuitem"
                    className="saved-view-menu-apply"
                    onClick={() => handleApply(v)}
                  >
                    <span>{v.name}</span>
                    <span className="saved-view-menu-owner">{v.owner_name}</span>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="saved-view-menu-footer">
            {saving ? (
              <div className="saved-view-menu-save">
                <input
                  ref={nameInputRef}
                  type="text"
                  className="saved-view-menu-save-input"
                  placeholder={t('savedViewsNamePlaceholder')}
                  value={name}
                  aria-label={t('savedViewsNamePlaceholder')}
                  onChange={(e) => {
                    setName(e.target.value)
                    if (saveError) clearSaveError()
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleSave()
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      e.stopPropagation()
                      setSaving(false)
                      setName('')
                    }
                  }}
                />
                <button
                  type="button"
                  className="saved-view-menu-save-confirm"
                  onClick={handleSave}
                  disabled={!name.trim()}
                >
                  {t('save')}
                </button>
                {saveError && (
                  <div className="saved-view-menu-error" role="alert">
                    {saveError}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="saved-view-menu-savecurrent"
                onClick={() => {
                  clearSaveError()
                  setSaving(true)
                }}
              >
                <Plus size={13} aria-hidden="true" />
                <span>{t('savedViewsSaveCurrent')}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
